import { filters, sound } from '@pixi/sound'
import { RecordingStatus, useVideoRecordingStore } from './useVideoRecordingStore'

export enum ShareType {
  None,
  Share,
  Download,
}

export class VideoRecorder {
  private mediaRecorder: MediaRecorder | null = null
  private recordedBlob: Blob | null = null
  private recordedChunks: Blob[] = []
  private currentStreamFilter: filters.StreamFilter | null = null
  private file: File | null = null
  private currentStreams: MediaStream[] = []
  startRecording() {
    try {
      if (useVideoRecordingStore.getState().status === RecordingStatus.Recording) {
        console.warn('Recording is already in progress')
        return
      }

      useVideoRecordingStore.setState({
        status: RecordingStatus.Recording,
      })

      this.mediaRecorder = this.createMediaRecorder()
      this.recordedChunks = []
      this.mediaRecorder.start()

      window.addEventListener('visibilitychange', this.forceCleanup, { once: true })
      window.addEventListener('orientationchange', this.forceCleanup, { once: true })
      window.addEventListener('resize', this.forceCleanup, { once: true })
    }
    catch (e) {
      console.error('Error starting recording:', e)
      this.forceCleanup()
    }
  }

  onDataAvailable = (e: BlobEvent) => {
    // Only process if we're still recording and haven't already processed data
    // console.log('onDataAvailable: ', e.data.size)

    if (this.mediaRecorder && e.data.size > 0 && useVideoRecordingStore.getState().status === RecordingStatus.Recording) {
      this.recordedChunks.push(e.data)
      this.recordedBlob = new Blob(this.recordedChunks, { type: this.mediaRecorder.mimeType })
      useVideoRecordingStore.setState({
        status: RecordingStatus.RecordingDone,
      })
      this.mediaRecorder.removeEventListener('dataavailable', this.onDataAvailable)
      // Remove the event listener after successful processing
    }
  }

  createMediaRecorder() {
    const canvas = document.querySelector('canvas') as HTMLCanvasElement
    const canvasStream = canvas.captureStream(30) // 30 fps (adjust as needed)

    // Create a StreamFilter to capture audio from PixiJS Game
    this.currentStreamFilter = new filters.StreamFilter()
    sound.filtersAll = [this.currentStreamFilter] // Apply the filter to all PixiJS sound
    const audioStream = this.currentStreamFilter.stream // Get the audio stream from the filter
    // Combine canvas video stream and audio stream
    const combinedStream = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...audioStream.getAudioTracks(),
    ])

    const options: MediaRecorderOptions = {}
    if (MediaRecorder.isTypeSupported('video/mp4')) {
      options.mimeType = 'video/mp4'
    }

    const mediaRecorder = new MediaRecorder(combinedStream, options)

    mediaRecorder.addEventListener('dataavailable', this.onDataAvailable)
    mediaRecorder.onerror = (error) => {
      console.error('MediaRecorder error:', error)
      this.forceCleanup() // Clean up resources on error
    }

    return mediaRecorder
  }

  async stopRecording() {
    try {
      if (this.mediaRecorder) {
        this.mediaRecorder.stop()
      }
      // else {
      //   console.log('Not in recording status, skipping stop')
      // }
    }
    catch (e) {
      console.error('Error stopping recording:', e)
      this.forceCleanup()
    }
    finally {
      // console.log('remove event listeners')
      window.removeEventListener('visibilitychange', this.forceCleanup)
      window.removeEventListener('orientationchange', this.forceCleanup)
      window.removeEventListener('resize', this.forceCleanup)
    }
  }

  async downloadVideo(filename = 'canvas-recording'): Promise<void> {
    if (!this.recordedBlob) {
      throw new Error('saveVideo called but no recorded video is available')
    }

    // Always create a new Blob and object URL for each download
    const blob = new Blob([this.recordedBlob], { type: this.recordedBlob.type })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.style.display = 'none'
    a.href = url
    const downloadName = `${filename}-${new Date().toISOString()}.${this.getFileExtension()}`
    a.download = downloadName
    a.id = downloadName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  getShareType(): ShareType {
    if (useVideoRecordingStore.getState().status !== RecordingStatus.RecordingDone || !this.recordedBlob) {
      return ShareType.None
    }
    // Check file size (limit to 50MB for better compatibility)
    // const maxFileSize = 50 * 1024 * 1024 // 50MB
    // if (this.recordedBlob.size > maxFileSize) {
    //   console.warn(`Video file size (${this.recordedBlob.size} bytes) exceeds recommended limit. Sharing may fail.`)
    //   return ShareType.Download
    // }

    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    const isAndroid = /Android/i.test(navigator.userAgent)

    if (!isMobile || isAndroid) {
      return ShareType.Download
    }

    // Create file with proper MIME type handling
    const mimeType = this.mediaRecorder?.mimeType || 'video/webm'
    const fileExtension = this.getFileExtension()
    const fileName = `recording-${Date.now()}.${fileExtension}`

    this.file = new File([this.recordedBlob], fileName, {
      type: mimeType,
      lastModified: Date.now(),
    })

    // Check if the browser can share this specific file
    if (navigator.canShare && navigator.canShare({ files: [this.file] })) {
      return ShareType.Share
    }

    return ShareType.Download
  }

  shareVideo(_title = 'My Recording', _text = 'Check out this recording!'): Promise<void | string> {
    return this.file
      ? navigator.share({
          title: _title,
          text: _text,
          files: [this.file],
        })
      : Promise.reject('no file')
  }

  // Public method to force cleanup (useful for slot games)
  forceCleanup = (): void => {
    try {
      // console.log('Resources cleaned up')
      // Clean up MediaRecorder event listeners
      if (this.mediaRecorder) {
        this.mediaRecorder.removeEventListener('dataavailable', this.onDataAvailable)
        this.mediaRecorder.stop()
        this.mediaRecorder = null
      }

      this.currentStreams.forEach((stream) => {
        stream.getTracks().forEach((track) => {
          track.stop()
        })
      })

      this.currentStreams = []
      // Remove StreamFilter from PixiJS sound system
      if (this.currentStreamFilter) {
        sound.filtersAll = [] // Reset all filters to handle tab switching issues
        this.currentStreamFilter.destroy()
        this.currentStreamFilter = null
      }

      this.recordedBlob = null
      this.file = null
      useVideoRecordingStore.setState({
        status: RecordingStatus.Init,
      })
      // console.log('Force cleanup completed')
    }
    catch (e) {
      console.error('Error during force cleanup:', e)
    }
  }

  getFileExtension() {
    // Determine file extension from mediaRecorder's mimeType if possible
    let fileExtension = 'webm'
    if (this.mediaRecorder && this.mediaRecorder.mimeType) {
      // console.log('actual mediaRecorder.mimeType: ', this.mediaRecorder.mimeType)
      const match = this.mediaRecorder.mimeType.match(/video\/(\w+)/)
      // console.log('match: ', match)
      if (match && match[1]) {
        fileExtension = match[1]
      }
    }
    // console.log('fileExtension: ', fileExtension)
    return fileExtension
  }
}
